import { useEffect, useState, useCallback } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { Accessibility, Check } from 'lucide-react'

export type FontScale = 'normal' | 'large' | 'xlarge'
export type ContrastMode = 'normal' | 'high'

export interface A11ySettings {
  fontSize: FontScale
  contrast: ContrastMode
}

const STORAGE_KEY = 'a11y-settings'

const DEFAULT_SETTINGS: A11ySettings = {
  fontSize: 'normal',
  contrast: 'normal',
}

export function loadA11ySettings(): A11ySettings {
  if (typeof window === 'undefined') return DEFAULT_SETTINGS
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULT_SETTINGS
    const parsed = JSON.parse(raw) as Partial<A11ySettings>
    return {
      fontSize: (parsed.fontSize === 'large' || parsed.fontSize === 'xlarge') ? parsed.fontSize : 'normal',
      contrast: parsed.contrast === 'high' ? 'high' : 'normal',
    }
  } catch {
    return DEFAULT_SETTINGS
  }
}

export function applyA11ySettings(settings: A11ySettings): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  if (settings.fontSize === 'normal') {
    root.removeAttribute('data-font-scale')
  } else {
    root.setAttribute('data-font-scale', settings.fontSize)
  }
  if (settings.contrast === 'normal') {
    root.removeAttribute('data-contrast')
  } else {
    root.setAttribute('data-contrast', settings.contrast)
  }
}

const FONT_LABELS: Record<FontScale, { label: string; desc: string }> = {
  normal: { label: 'A', desc: '100%' },
  large: { label: 'A+', desc: '115%' },
  xlarge: { label: 'A++', desc: '130%' },
}

export default function AccessibilityToggle() {
  const [settings, setSettings] = useState<A11ySettings>(DEFAULT_SETTINGS)

  useEffect(() => {
    const loaded = loadA11ySettings()
    setSettings(loaded)
    applyA11ySettings(loaded)
  }, [])

  const update = useCallback((patch: Partial<A11ySettings>) => {
    setSettings((prev) => {
      const next = { ...prev, ...patch }
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
      } catch {
        // ignore storage errors
      }
      applyA11ySettings(next)
      return next
    })
  }, [])

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
          aria-label="Dostępność"
          title="Dostępność"
        >
          <Accessibility className="w-5 h-5" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className="z-[60] min-w-[240px] rounded-lg border border-gray-200 bg-white p-2 shadow-lg"
        >
          <DropdownMenu.Label className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">
            Rozmiar tekstu
          </DropdownMenu.Label>
          <DropdownMenu.RadioGroup
            value={settings.fontSize}
            onValueChange={(value) => update({ fontSize: value as FontScale })}
          >
            {(Object.keys(FONT_LABELS) as FontScale[]).map((scale) => (
              <DropdownMenu.RadioItem
                key={scale}
                value={scale}
                className="flex cursor-pointer items-center justify-between rounded-md px-2 py-2 text-sm outline-none hover:bg-gray-100 focus:bg-gray-100 data-[state=checked]:font-semibold"
              >
                <span className="flex items-center gap-3">
                  <span className="inline-flex w-10 items-center justify-center font-bold text-primary">
                    {FONT_LABELS[scale].label}
                  </span>
                  <span className="text-gray-700">{FONT_LABELS[scale].desc}</span>
                </span>
                <DropdownMenu.ItemIndicator>
                  <Check className="h-4 w-4 text-primary" />
                </DropdownMenu.ItemIndicator>
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>

          <DropdownMenu.Separator className="my-2 h-px bg-gray-200" />

          <DropdownMenu.Label className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">
            Kontrast
          </DropdownMenu.Label>
          <DropdownMenu.RadioGroup
            value={settings.contrast}
            onValueChange={(value) => update({ contrast: value as ContrastMode })}
          >
            <DropdownMenu.RadioItem
              value="normal"
              className="flex cursor-pointer items-center justify-between rounded-md px-2 py-2 text-sm outline-none hover:bg-gray-100 focus:bg-gray-100 data-[state=checked]:font-semibold"
            >
              <span className="text-gray-700">Normalny</span>
              <DropdownMenu.ItemIndicator>
                <Check className="h-4 w-4 text-primary" />
              </DropdownMenu.ItemIndicator>
            </DropdownMenu.RadioItem>
            <DropdownMenu.RadioItem
              value="high"
              className="flex cursor-pointer items-center justify-between rounded-md px-2 py-2 text-sm outline-none hover:bg-gray-100 focus:bg-gray-100 data-[state=checked]:font-semibold"
            >
              <span className="text-gray-700">Wysoki</span>
              <DropdownMenu.ItemIndicator>
                <Check className="h-4 w-4 text-primary" />
              </DropdownMenu.ItemIndicator>
            </DropdownMenu.RadioItem>
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
